import React, { useEffect, useMemo, useState } from 'react';
import { Links } from 'application/constants/links';
import { Loading } from 'components/uikit/suspense/Loading';
import { QueryFactory } from 'services/api';
import {
  TextButton,
  TextButtonColor,
} from 'components/uikit/buttons/TextButton';
import { ButtonColor } from 'components/uikit/buttons/Button';
import {
  RevisionModal,
  RevisionModalMode,
} from 'pages/authorized/revisions/components/revisionModal/RevisionModal';
import { DropDownInput } from 'components/uikit/inputs/dropdown/DropDownInput';
import EditIcon from 'assets/icons/edit.svg?react';
import ExportIcon from 'assets/icons/export.svg?react';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import styles from './ProjectDetailsPage.module.scss';
import { VariableList } from 'pages/authorized/variables/VariablesList';
import { emptyArray } from 'helpers/empty-array';
import { useModal } from 'components/uikit/modal/useModal';
import { Link } from '@mui/material';
import { RoleBar } from '../components/roleBar/RoleBar.tsx';
import { useSharePointExport } from '../components/useSharePointExport.tsx';
import { DocumentList } from 'pages/authorized/documents/documentList/DocumentList.tsx';
import { PermissionTextButton } from 'helpers/projectPermissions/components/PermissionTextButton.tsx';
import {
  OperationType,
  ProjectResourceType,
} from 'services/api/api-client.types.ts';
import { permission } from 'helpers/projectPermissions/types/projectPermission.ts';
import { useHasProjectPermission } from 'helpers/projectPermissions/useHasProjectPermission.ts';
import { useVariablesContext } from 'pages/authorized/variables/variablesContext/VariablesContext.tsx';

export const ProjectDetailsPage: React.FC = () => {
  const i18n = useScopedTranslation('Page.Projects.ProjectDetailsPage');
  const params = Links.Authorized.ProjectDetails.useParams();
  const modal = useModal();
  const selectedRevisionId = params.queryParams.releaseId;
  const projectId = params.projectId;
  const revisionsQuery = QueryFactory.RevisionQuery.useSearchQuery({
    projectId,
  });
  const revisions = revisionsQuery.data?.data ?? emptyArray();

  const [isLoading, setIsLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { isSharePointExporting, exportToSharePoint } = useSharePointExport({
    projectId,
    revisionId: selectedRevisionId!,
  });

  const selectedRevision = useMemo(() => {
    return revisions.find((x) => x.id === selectedRevisionId);
  }, [revisions, selectedRevisionId]);

  const basedOnRevisionVersion = useMemo(() => {
    return revisions.find((x) => x.id === selectedRevision?.basedOnRevisionId)
      ?.version;
  }, [revisions, selectedRevision]);

  useEffect(
    function setLastRevision() {
      if (revisions.length && !selectedRevision) {
        params.setQueryParams(
          { releaseId: revisions[revisions.length - 1].id },
          'replace',
        );
      }
    },
    [revisions],
  );

  const dropdownClasses = {
    option: styles.revisionSelectorOption,
    paper: styles.revisionSelectorPaper,
    popper: styles.revisionSelectorPopper,
  };

  const { variablesQuery, refreshVariables } = useVariablesContext();

  const hasPermission = useHasProjectPermission();

  const hasPermissionToExport = hasPermission([
    permission(ProjectResourceType.Revision, OperationType.Update),
  ]);

  return (
    <>
      <Loading
        className={styles.loader}
        loading={revisionsQuery.isLoading || isSharePointExporting || isLoading}
      >
        <div
          className={styles.mainContainer}
          data-test-id="project-details-page"
        >
          <div className={styles.body}>
            <div className={styles.dropDownContainer}>
              <div className={styles.revisionBlock}>
                <div className={styles.revisionRow}>
                  <div className={styles.revisionField}>
                    {i18n.t('revision')}
                  </div>
                  <DropDownInput
                    testId="revisions-dropdown"
                    className={styles.revisionSelector}
                    classes={dropdownClasses}
                    value={selectedRevision ?? undefined}
                    required={true}
                    options={revisionsQuery.data?.data ?? []}
                    getOptionLabel={(x) => x.version}
                    isOptionEqualToValue={(x, y) => x.id === y.id}
                    popupHeader={
                      <PermissionTextButton
                        testId="add-revision-button"
                        className={styles.revisionSelectorHeader}
                        color={TextButtonColor.Primary}
                        onClick={() => setIsModalOpen(true)}
                        title={i18n.t('new')}
                        permissions={[
                          permission(
                            ProjectResourceType.Revision,
                            OperationType.Create,
                          ),
                        ]}
                      />
                    }
                    onValueChanged={(val) => {
                      params.setQueryParams({
                        releaseId: val?.id,
                      });
                    }}
                    autosizeInputWidth={true}
                    useVirtualization={false}
                  />
                  <PermissionTextButton
                    className={styles.editButton}
                    color={TextButtonColor.Primary}
                    onClick={() => setIsEditModalOpen(true)}
                    icon={<EditIcon />}
                    permissions={[
                      permission(
                        ProjectResourceType.Revision,
                        OperationType.Update,
                      ),
                    ]}
                  />
                </div>
                <div
                  className={styles.revisionMeta}
                  data-test-id="revision-meta"
                >
                  {selectedRevision?.release && (
                    <div className={styles.releaseItem}>
                      <span className={styles.revisionMetaLabel}>
                        {i18n.t('release_name', {
                          release: selectedRevision.release,
                        })}
                      </span>
                    </div>
                  )}
                  {basedOnRevisionVersion && (
                    <div className={styles.basedOnItem}>
                      <span className={styles.revisionMetaLabel}>
                        {i18n.t('based_on', {
                          revision: basedOnRevisionVersion,
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <TextButton
                className={styles.sharePointButton}
                title={i18n.t('ExportSharePoint.title')}
                color={TextButtonColor.Primary}
                icon={<ExportIcon />}
                onClick={async () => {
                  const handler = selectedRevisionId
                    ? () =>
                        QueryFactory.SettingsClient.settingsForSpecificRevision(
                          selectedRevisionId,
                        )
                    : QueryFactory.SettingsClient.settingsDto;
                  const url = (await handler()).sharePointUrl.replace(
                    '%20',
                    ' ',
                  );
                  const result = await modal.showMultiButton({
                    title: i18n.t('ExportSharePoint.title'),
                    text: (
                      <div>
                        <span>
                          {i18n.t(
                            'ExportSharePoint.export_message_before_revision',
                          )}
                          {selectedRevision?.version}
                          {i18n.t(
                            'ExportSharePoint.export_message_after_revision',
                          )}
                        </span>
                        <Link
                          className={styles.sharePointLink}
                          href={url}
                          target="_blank"
                        >
                          {url}
                        </Link>
                      </div>
                    ),
                    buttons: [
                      {
                        id: 'cancel',
                        text: i18n.t('ExportSharePoint.cancel_button'),
                        color: ButtonColor.Secondary,
                      },
                      {
                        id: 'export',
                        text: i18n.t('ExportSharePoint.confirm_button'),
                        disabled: !hasPermissionToExport,
                        tooltipMessage: i18n.t(
                          'ExportSharePoint.not_enough_permissions',
                        ),
                      },
                    ],
                  });
                  if (result === 'export') {
                    await exportToSharePoint();
                  }
                }}
              />
            </div>
            {selectedRevisionId && projectId && variablesQuery.data?.data && (
              <DocumentList
                revisionId={selectedRevisionId}
                projectId={projectId}
                revisionVariables={variablesQuery.data.data}
              />
            )}
          </div>
          <div className={styles.leftContainer}>
            {projectId && (
              <div className={styles.roleContainer}>
                <RoleBar
                  projectId={projectId}
                  revisionId={selectedRevisionId!}
                />
              </div>
            )}
            {selectedRevisionId && projectId && (
              <div className={styles.variableContainer}>
                <VariableList
                  projectId={projectId}
                  revisionId={selectedRevisionId}
                  isDocumentEditingLocked={false}
                  onVariableUpdate={async (variableIds: number[]) => {
                    if (!selectedRevisionId) return;

                    setIsLoading(true);
                    await QueryFactory.RevisionClient.updateVariables(
                      selectedRevisionId,
                      variableIds,
                    );

                    await refreshVariables();

                    setIsLoading(false);
                  }}
                  onVariableClick={undefined}
                />
              </div>
            )}
          </div>
        </div>
      </Loading>

      {projectId && (
        <RevisionModal
          mode={RevisionModalMode.Add}
          projectId={projectId}
          onRevisionCreated={(revisionId) => {
            params.setQueryParams({ releaseId: revisionId });
          }}
          onClose={() => setIsModalOpen(false)}
          isOpen={isModalOpen}
          isBlocking={true}
        />
      )}

      {selectedRevision && projectId && (
        <RevisionModal
          mode={RevisionModalMode.Edit}
          projectId={projectId}
          revision={selectedRevision}
          basedOnRevisionVersion={basedOnRevisionVersion}
          onClose={() => setIsEditModalOpen(false)}
          isOpen={isEditModalOpen}
          isBlocking={true}
        />
      )}
    </>
  );
};
